import React, { useEffect, useState } from "react";
import Pencil from "../../../../images/pencil.png";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from "react-redux";
import {
  resetState,
  setCandidateId,
  setEmergencyContacts,
} from "../../../../features/candidate/personalSlice";
import Loader from "../../../../components/Loader";
import { createCandidate, createEmergencyContact } from "../../../../api/candidiate";
import { mapToOption, formatPhone, formatDateCST, formatTimeCST } from "../../../../utlis";
import { useCandidateForm } from "../../CandidateFormContext";

const flattenObject = (obj, parentKey = '') => {
  let result = {};

  for (const key in obj) {
    const newKey = key;

    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(result, flattenObject(obj[key], newKey));
    } else {
      result[newKey] = obj[key];
    }
  }

  return result;
};

const EmergencyContacts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const { isCreating, markTabComplete, resetCandidateData, hasUnsavedChanges, isSaving, saveAllChanges } = useCandidateForm();

  const candidateId = useSelector(state => state.personal.candidateId);

  const referenceData = useSelector((state) => state.reference);

  const { contactInfo1, contactInfo2 } = useSelector(
    (state) => state.personal.emergencyContacts
  );

  const personalInfo = useSelector((state) => state.personal);

  const [contact1, setContact1] = useState(contactInfo1);
  const [contact2, setContact2] = useState(contactInfo2);

  // Sync Redux → local state when data is loaded externally (e.g., edit candidate fetch)
  useEffect(() => {
    if (JSON.stringify(contactInfo1) !== JSON.stringify(contact1)) {
      setContact1(contactInfo1);
    }
  }, [contactInfo1]);

  useEffect(() => {
    if (JSON.stringify(contactInfo2) !== JSON.stringify(contact2)) {
      setContact2(contactInfo2);
    }
  }, [contactInfo2]);

  const handleChange1 = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Format phone number
    if (name === 'contactPhoneNum') {
      const digits = value.replace(/\D/g, '');
      formattedValue = digits.slice(0, 10);
    }

    setContact1((pervData) => ({
      ...pervData,
      [name]: formattedValue,
    }));
  };

  const handleChange2 = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Format phone number
    if (name === 'contactPhoneNum') {
      const digits = value.replace(/\D/g, '');
      formattedValue = digits.slice(0, 10);
    }

    setContact2((pervData) => ({
      ...pervData,
      [name]: formattedValue,
    }));
  };

  // Validate Emergency Contact 1 (required)
  const validateContact1 = () => {
    const requiredFields = [
      { field: 'firstName', label: 'First Name' },
      { field: 'lastName', label: 'Last Name' },
      { field: 'contactPhoneNum', label: 'Phone Number' },
      { field: 'email', label: 'Email' },
      { field: 'contactRelation', label: 'Contact Relation' },
    ];

    for (const { field, label } of requiredFields) {
      if (!contact1[field] || contact1[field].toString().trim() === '') {
        toast.error(`Emergency Contact 1: ${label} is required`);
        return false;
      }
    }

    // Validate phone number length
    if (contact1.contactPhoneNum && contact1.contactPhoneNum.replace(/\D/g, '').length !== 10) {
      toast.error('Emergency Contact 1: Phone Number must be 10 digits');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    // Validate Emergency Contact 1
    if (!validateContact1()) {
      return;
    }
    const parseBody = flattenObject({...personalInfo,emergencyContacts:{}});
    setLoading(true);
    try {
      const response = await createCandidate(parseBody);

      const newCandidateId = response.data.data.candidateId;
      dispatch(setCandidateId(newCandidateId));

      const isContactEmpty = (c) => !c.firstName && !c.lastName && !c.contactPhoneNum && !c.email && !c.contactRelation;
      const ecPromises = [];
      if (!isContactEmpty(contactInfo1)) ecPromises.push(createEmergencyContact(contactInfo1, newCandidateId, 1));
      if (!isContactEmpty(contactInfo2)) ecPromises.push(createEmergencyContact(contactInfo2, newCandidateId, 2));
      if (ecPromises.length) await Promise.all(ecPromises);

      toast.success('Candidate created successfully! All sections are now available.');

      // Mark tab complete and switch to edit mode
      markTabComplete('emergency-contacts');
      resetCandidateData(); // Switch context to edit mode

      // Navigate to Personal section - user will now see all nav sections
      navigate('/candidate/add-candidate/personal');
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 400) {
        // Validation error
        const detail = data?.details || data?.message || 'Invalid request payload';
        toast.error(`Validation Error: ${detail}`);
        if (data?.additionalAttributes?.length) {
          toast.warn(`Unknown fields: ${data.additionalAttributes.join(', ')}`);
        }
      } else if (status === 409) {
        // Duplicate conflict
        const conflicting = data?.conflictingFields;
        if (conflicting) {
          const fields = Object.keys(conflicting).join(', ');
          toast.error(`Conflict: A candidate with this ${fields} already exists.`);
        } else {
          toast.error(data?.message || 'A candidate with these details already exists.');
        }
      } else if (status === 401) {
        toast.error('Unauthorized: Please log in again.');
      } else {
        toast.error(data?.message || 'Failed to save candidate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setEmergencyContacts({ contact1, contact2 }));
  }, [contact1, contact2]);

  // Handle Previous button click
  const handlePrevious = () => {
    navigate('/candidate/add-candidate/personal/profile');
  };

  return (
    <div className="">
      {isLoading && <Loader />}
      <from className="grid grid-cols-2 gap-6 mt-5 text-sm font-normal text-text-hint">
        <div className="flex flex-col gap-y-6">
          <h3 className="text-text-default">Emergency Contacts 1</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="">
              <label className="">
                First Name<span className="text-red-500">*</span>
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange1}
                  value={contact1.firstName}
                  name="firstName"
                  placeholder="First Name"
                  required
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
            <div className="">
              <label className="">
                Last Name<span className="text-red-500">*</span>
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange1}
                  value={contact1.lastName}
                  name="lastName"
                  placeholder="Last Name"
                  required
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
            <div className="">
              <label className="">
                Contacts Phone Number<span className="text-red-500">*</span>
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange1}
                  value={formatPhone(contact1.contactPhoneNum)}
                  name="contactPhoneNum"
                  placeholder="(999) 999 9999"
                  required
                  maxLength={15}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="">
                Email<span className="text-red-500">*</span>
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange1}
                  value={contact1.email}
                  name="email"
                  placeholder="youremail@gmail.com"
                  required
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="">
              <label className="">
                Contacts Relations<span className="text-red-500">*</span>
              </label>
              <select
                onChange={handleChange1}
                value={contact1.contactRelation}
                name="contactRelation"
                className="flex justify-center items-center text-black focus:ring-0 px-3 py-2 mt-1 border border-neutral-500 rounded-lg outline-none w-full "
              >
                {mapToOption(referenceData.relations?.relation || [], 'type').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Audit Footer - Contact 1 */}
          {candidateId && !isCreating && (
            <div className="grid grid-cols-2 gap-1.5 mt-2 pt-3 border-t border-gray-200">
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Created By</label>
                <p className="text-sm text-gray-600">{contactInfo1.createdBy || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Updated By</label>
                <p className="text-sm text-gray-600">{contactInfo1.updatedBy || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Date Created</label>
                <p className="text-sm text-gray-600">
                  {contactInfo1.dateCreated ? <>{formatDateCST(contactInfo1.dateCreated)} <span className="text-xs text-gray-400 ml-1">{formatTimeCST(contactInfo1.dateCreated)}</span></> : 'N/A'}
                </p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Last Updated</label>
                <p className="text-sm text-gray-600">
                  {contactInfo1.lastUpdated ? <>{formatDateCST(contactInfo1.lastUpdated)} <span className="text-xs text-gray-400 ml-1">{formatTimeCST(contactInfo1.lastUpdated)}</span></> : 'N/A'}
                </p>
              </div>
            </div>
          )}
        </div>
        {/* Emergency contact 2 */}

        <div className="flex flex-col gap-y-6">
          <h3 className="text-text-default">Emergency Contacts 2 (Optional)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="">
              <label className="">
                First Name
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange2}
                  value={contact2.firstName}
                  name="firstName"
                  placeholder="First Name"
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
            <div className="">
              <label className="">
                Last Name
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange2}
                  value={contact2.lastName}
                  name="lastName"
                  placeholder="Last Name"
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
            <div className="">
              <label className="">
                Contacts Phone Number
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange2}
                  value={formatPhone(contact2.contactPhoneNum)}
                  name="contactPhoneNum"
                  placeholder="(999) 999 9999"
                  maxLength={15}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="">
                Email
              </label>
              <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
                <input
                  onChange={handleChange2}
                  value={contact2.email}
                  name="email"
                  placeholder="youremail@gmail.com"
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="">
              <label className="">
                Contacts Relations
              </label>
              <select
                onChange={handleChange2}
                value={contact2.contactRelation}
                name="contactRelation"
                className="flex justify-center items-center text-black focus:ring-0 px-3 py-2 mt-1 border border-neutral-500 rounded-lg outline-none w-full "
              >
                {mapToOption(referenceData.relations?.relation || [], 'type').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Audit Footer - Contact 2 */}
          {candidateId && !isCreating && (
            <div className="grid grid-cols-2 gap-1.5 mt-2 pt-3 border-t border-gray-200">
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Created By</label>
                <p className="text-sm text-gray-600">{contactInfo2.createdBy || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Updated By</label>
                <p className="text-sm text-gray-600">{contactInfo2.updatedBy || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Date Created</label>
                <p className="text-sm text-gray-600">
                  {contactInfo2.dateCreated ? <>{formatDateCST(contactInfo2.dateCreated)} <span className="text-xs text-gray-400 ml-1">{formatTimeCST(contactInfo2.dateCreated)}</span></> : 'N/A'}
                </p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Last Updated</label>
                <p className="text-sm text-gray-600">
                  {contactInfo2.lastUpdated ? <>{formatDateCST(contactInfo2.lastUpdated)} <span className="text-xs text-gray-400 ml-1">{formatTimeCST(contactInfo2.lastUpdated)}</span></> : 'N/A'}
                </p>
              </div>
            </div>
          )}
        </div>
      </from>
      <div className="w-full py-10 px-8 flex justify-end gap-5">
        <button
          onClick={handlePrevious}
          className="text-secondary-700 bg-white border border-secondary-700 py-2 px-5 rounded-full text-base hover:bg-gray-50 transition"
        >
          Previous
        </button>
        {!isCreating && hasUnsavedChanges && (
          <button
            disabled={isSaving}
            onClick={async () => {
              try {
                await saveAllChanges();
              } catch (e) {
                // error toast handled by context
              }
            }}
            className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        )}
        {isCreating && (
          <button
            onClick={handleSubmit}
            className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base hover:bg-secondary-800 transition"
          >
            Save Candidate
          </button>
        )}
      </div>
    </div>
  );
};

export default EmergencyContacts;
